import type {HierarchyTreeNode} from './HierarchyTree';

/** Ids to expand so the node shows: its ancestors, plus the node itself when it has children. */
export const findHierarchyExpansionPath = (
  nodes: HierarchyTreeNode[],
  id: string,
  trail: string[] = []
): string[] | undefined => {
  for (const node of nodes) {
    if (node.id === id) return node.children?.length ? [...trail, id] : trail;
    if (node.children) {
      const found = findHierarchyExpansionPath(node.children, id, [
        ...trail,
        node.id,
      ]);
      if (found) return found;
    }
  }
  return undefined;
};

/** Ids from the root to the selected node's parent. Empty when the node is absent. */
export const findHierarchyAncestorIds = (
  nodes: HierarchyTreeNode[],
  id: string
): string[] =>
  (findHierarchyExpansionPath(nodes, id) ?? []).filter(nodeId => nodeId !== id);

/** Nodes whose label, or any descendant's label, contains the lowercase `term`; ancestors survive. */
export const filterHierarchyNodes = (
  nodes: HierarchyTreeNode[],
  term: string
): HierarchyTreeNode[] =>
  nodes.reduce<HierarchyTreeNode[]>((acc, node) => {
    const children = node.children
      ? filterHierarchyNodes(node.children, term)
      : [];
    if (node.label.toLowerCase().includes(term) || children.length > 0) {
      acc.push({...node, children: children.length > 0 ? children : undefined});
    }
    return acc;
  }, []);

export const collectHierarchyIds = (
  nodes: HierarchyTreeNode[],
  acc: string[] = []
): string[] => {
  nodes.forEach(node => {
    acc.push(node.id);
    if (node.children) collectHierarchyIds(node.children, acc);
  });
  return acc;
};
